import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';

export default function HalfDayWorkshopPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'Half Day Workshop: Practical AI for Your Workplace | Evernorth AI';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '3.5-hour hands-on AI training workshop for teams. Learn practical AI skills, prompt engineering, and real-world applications. Starting at $1,500 for 10 attendees in Northwest Georgia.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/workshops/half-day`);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Half Day Workshop: Practical AI for Your Workplace",
      "description": "A hands-on, interactive workshop that teaches employees how to apply AI to real work, communicate effectively with AI tools, and improve everyday tasks.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Evernorth AI",
        "areaServed": "Northwest Georgia"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/workshops/half-day`,
      "courseMode": "blended",
      "duration": "PT3H30M",
      "timeRequired": "PT3H30M",
      "educationalLevel": "Beginner to Intermediate",
      "audience": {
        "@type": "EducationalAudience",
        "educationalRole": "Business professionals"
      },
      "offers": {
        "@type": "Offer",
        "price": "1500",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "description": "Starting price for 10 attendees"
      },
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": [
          "onsite",
          "online"
        ],
        "instructor": {
          "@type": "Person",
          "name": "Evernorth AI Team"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/workshops/half-day-workshop.jpg"
            alt="Half Day Workshop: Practical AI for Your Workplace"
            title="Half Day AI Workshop for business teams"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/75 to-navy/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-gold/20 border border-gold/30 rounded-md">
              <span className="text-gold text-base font-semibold">Foundations Program</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
              Half Day Workshop
              <br />
              <span className="text-gold">Practical AI for Your Workplace</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-2xl">
              A hands-on, interactive workshop that teaches employees how to apply AI to real work,
              communicate effectively with AI tools, and improve everyday tasks.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="https://calendly.com/evernorthai/half-day-workshop"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
              >
                Book This Workshop
              </a>
              <div className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <i className="ri-time-line text-gold text-lg"></i>
                <span className="text-white font-medium text-sm whitespace-nowrap">3.5 Hours</span>
              </div>
              <div className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <i className="ri-money-dollar-circle-line text-gold text-lg"></i>
                <span className="text-white font-medium text-sm whitespace-nowrap">Starting at $1,500</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <div className="inline-block mb-4 px-4 py-2 bg-gold/10 rounded-md">
                <span className="text-navy text-base font-semibold">Overview</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">
                Build Confidence &amp; Clarity <span className="text-gold">In One Session</span>
              </h2>
              <div className="space-y-5 text-charcoal leading-relaxed">
                <p>
                  The Half Day Workshop is a focused learning session that helps teams move beyond
                  general awareness and into practical understanding and usage. It is designed for
                  organizations that want hands‑on education without committing to a full day.
                </p>
                <p>
                  Participants learn how AI can support common workplace tasks and processes, where
                  caution is needed, and how to approach AI use consistently across a team.
                </p>
                <p>
                  This workshop balances explanation with practice. Concepts are introduced in plain
                  language and reinforced through real world examples and guided discussion tailored
                  to how the organization works.
                </p>
                <p>
                  By the end of the session, participants have greater confidence using AI
                  responsibly, a clearer sense of appropriate use cases, and a shared framework for
                  thinking about AI at work.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="bg-gold/10 border border-gold/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg">
                    <i className="ri-check-double-line text-gold text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">Ready to Use Immediately</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  Attendees leave the workshop with skills ready to use <strong>that same day</strong>.
                  No technical background is required.
                </p>
              </div>

              <div className="bg-navy/5 border border-navy/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-global-line text-navy text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">Flexible Delivery</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  We offer this training <strong>virtually or in‑person</strong>, making it easy to fit
                  into your team’s schedule.
                </p>
              </div>

              <div className="bg-[#f8f8f9] border border-soft-gray/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-group-line text-navy text-xl"></i>
                  </div>
                  <h4 className="text-navy font-bold text-lg">For 10 Attendees</h4>
                </div>
                <p className="text-charcoal text-sm leading-relaxed">
                  Pricing starts at <strong>$1,500 for up to 10 attendees</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What’s Included */}
      <section className="py-20 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              What’s <span className="text-gold">Included</span>
            </h2>
            <p className="text-charcoal text-lg max-w-2xl mx-auto">
              Every attendee walks away with practical tools and resources
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-presentation-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Interactive Training</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                3.5 hours of hands‑on, guided instruction with real‑world examples tailored to your
                industry
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-book-open-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">AI Prompt Reference Guide</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                A comprehensive guide your team can reference long after the workshop ends
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-lightbulb-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Demo Examples</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                Real demonstrations from the workshop so your team can revisit and practice on their
                own
              </p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-soft-gray/30 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl mb-5">
                <i className="ri-discuss-line text-gold text-2xl"></i>
              </div>
              <h4 className="text-navy font-bold mb-2">Guided Discussion</h4>
              <p className="text-charcoal text-sm leading-relaxed">
                Tailored conversation around your organization’s specific workflows and use cases
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Note */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-navy/5 border border-navy/10 rounded-2xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 flex items-center justify-center bg-gold/20 rounded-xl flex-shrink-0">
                <i className="ri-settings-3-line text-gold text-2xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-navy mb-3">Custom Workshops Available</h3>
                <p className="text-charcoal leading-relaxed">
                  Custom workshops with industry‑specific examples and post‑workshop support calls are
                  available. We’ll tailor the content to your team’s unique challenges and goals so
                  every minute of the session is relevant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compare / CTA */}
      <section className="py-20 bg-[#f8f8f9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-soft-gray/30 shadow-sm flex flex-col">
              <div className="mb-6">
                <div className="text-sm text-gold font-semibold mb-2">Need More Depth?</div>
                <h3 className="text-2xl font-bold text-navy mb-3">Consider the Full Day Workshop</h3>
                <p className="text-charcoal leading-relaxed">
                  If your team needs deeper discussion, more hands‑on practice, and alignment across
                  roles, the Full Day Workshop provides 7 hours of comprehensive training.
                </p>
              </div>
              <div className="mt-auto">
                <Link
                  to="/workshops/full-day"
                  className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors cursor-pointer"
                >
                  View Full Day Workshop
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="bg-gradient-to-br from-navy to-[#062299] rounded-2xl p-8 flex flex-col">
              <div className="mb-6">
                <div className="text-sm text-gold font-semibold mb-2">Ready to Get Started?</div>
                <h3 className="text-2xl font-bold text-white mb-3">Book Your Half Day Workshop</h3>
                <p className="text-white/80 leading-relaxed">
                  Give your team practical AI skills they can use today. Reserve your half-day workshop now and start seeing real results.
                </p>
              </div>
              <div className="mt-auto">
                <a
                  href="https://calendly.com/evernorthai/half-day-workshop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3.5 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
                >
                  Book This Workshop
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
